import { useState, useEffect, useCallback } from 'react';
import { accountsApi } from '../services/api/endpoints/accounts';
import { IndexedDBService } from '../services/storage/IndexedDBService';
import { SyncService } from '../services/sync/SyncService';
import type { Account } from '../types/accounting';
import type { AccountSearchParams, AccountFilters } from '../types/filters';

export function useAccounts() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [filteredAccounts, setFilteredAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState<AccountFilters>({});

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async (searchParams?: AccountSearchParams) => {
    try {
      setLoading(true);
      
      // Load from IndexedDB first
      const localAccounts = await IndexedDBService.getAll<Account>('accounts');
      if (localAccounts.length > 0) {
        const filtered = applyFilters(localAccounts, searchParams);
        setAccounts(localAccounts);
        setFilteredAccounts(filtered.data);
        setPagination(filtered.pagination);
        setLoading(false);
      }

      // If online, fetch from API and update local storage
      if (navigator.onLine) {
        const response = await accountsApi.getAll(searchParams);
        if (response.success && response.data) {
          setAccounts(response.data.data);
          setFilteredAccounts(response.data.data);
          setPagination({
            page: response.data.page,
            pageSize: response.data.pageSize,
            total: response.data.total,
            totalPages: response.data.totalPages
          });
          
          // Update IndexedDB
          for (const account of response.data.data) {
            await IndexedDBService.update('accounts', account);
          }
        }
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (accountsList: Account[], searchParams?: AccountSearchParams) => {
    let filtered = [...accountsList];
    
    if (searchParams?.search) {
      const search = searchParams.search.toLowerCase();
      filtered = filtered.filter(account => 
        account.code.toLowerCase().includes(search) ||
        account.name.toLowerCase().includes(search)
      );
    }
    
    if (searchParams?.type && searchParams.type !== 'all') {
      filtered = filtered.filter(account => account.type === searchParams.type);
    }
    
    if (searchParams?.standard && searchParams.standard !== 'all') {
      filtered = filtered.filter(account => account.standard === searchParams.standard);
    }
    
    if (searchParams?.isAnalytic !== undefined) {
      filtered = filtered.filter(account => account.isAnalytic === searchParams.isAnalytic);
    }
    
    // Sorting
    if (searchParams?.sortBy) {
      filtered.sort((a, b) => {
        const order = searchParams.sortOrder === 'desc' ? -1 : 1;
        if (searchParams.sortBy === 'code') {
          return a.code.localeCompare(b.code) * order;
        } else if (searchParams.sortBy === 'name') {
          return a.name.localeCompare(b.name) * order;
        } else if (searchParams.sortBy === 'type') {
          return a.type.localeCompare(b.type) * order;
        }
        return 0;
      });
    }
    
    // Pagination
    const page = searchParams?.page || 1;
    const pageSize = searchParams?.pageSize || 20;
    const total = filtered.length;
    const totalPages = Math.ceil(total / pageSize);
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    
    return {
      data: filtered.slice(startIndex, endIndex),
      pagination: {
        page,
        pageSize,
        total,
        totalPages
      }
    };
  };

  const applyLocalFilters = useCallback((newFilters: AccountFilters) => {
    setFilters(newFilters);
    const searchParams: AccountSearchParams = {
      ...newFilters,
      page: 1, // Reset to first page when filters change
      pageSize: pagination.pageSize
    };
    loadAccounts(searchParams);
  }, [pagination.pageSize]);

  const addAccount = async (data: Partial<Account>): Promise<Account> => {
    const newAccount: Account = {
      id: crypto.randomUUID(),
      code: data.code!,
      name: data.name!,
      type: data.type!,
      standard: data.standard!,
      isAnalytic: data.isAnalytic || false
    };

    // Save to IndexedDB
    await IndexedDBService.add('accounts', newAccount);
    setAccounts(prev => [...prev, newAccount]);

    // Add to sync queue if offline
    if (!navigator.onLine) {
      await SyncService.addToSyncQueue({
        id: newAccount.id,
        type: 'CREATE',
        entity: 'accounts',
        data: newAccount
      });
    } else {
      // Try to sync immediately if online
      try {
        const response = await accountsApi.create(newAccount);
        if (response.success && response.data) {
          return response.data;
        }
      } catch (error) {
        console.error('Error creating account:', error);
      }
    }

    return newAccount;
  };

  const updateAccount = async (id: string, data: Partial<Account>): Promise<Account> => {
    const existingAccount = accounts.find(acc => acc.id === id);
    if (!existingAccount) {
      throw new Error('Account not found');
    }

    const updatedAccount: Account = { ...existingAccount, ...data };

    // Update local storage immediately
    await IndexedDBService.update('accounts', updatedAccount);
    
    // Update local state
    const updatedAccounts = accounts.map(acc => 
      acc.id === id ? updatedAccount : acc
    );
    setAccounts(updatedAccounts);
    setFilteredAccounts(updatedAccounts);

    // Try to sync with API if online
    if (navigator.onLine) {
      try {
        const response = await accountsApi.update(id, data);
        if (response.success && response.data) {
          return response.data;
        }
      } catch (error) {
        console.error('Error updating account:', error);
      }
    }

    return updatedAccount;
  };

  const deleteAccount = async (id: string): Promise<void> => {
    // Remove from local storage immediately
    await IndexedDBService.delete('accounts', id);
    
    // Update local state
    const updatedAccounts = accounts.filter(acc => acc.id !== id);
    setAccounts(updatedAccounts);
    setFilteredAccounts(updatedAccounts);

    // Try to sync with API if online
    if (navigator.onLine) {
      try {
        await accountsApi.delete(id);
      } catch (error) {
        console.error('Error deleting account:', error);
      }
    }
  };

  return {
    accounts: filteredAccounts,
    allAccounts: accounts,
    loading,
    pagination,
    filters,
    addAccount,
    updateAccount,
    deleteAccount,
    loadAccounts,
    applyFilters: applyLocalFilters
  };
}